import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

// ─── Types ───────────────────────────────────────────────────────────────────

export type SessionMode = "drill" | "mixed" | "review";

export type SessionFilters = {
  street?: string;
  position?: string;
  scenario?: string;
  author?: string;
  level?: string;
};

export type SessionResult = {
  sceneId: string;
  title: string;
  correct: boolean;
  timeMs: number;
  chosenLabel: string;
  correctLabel: string;
  optionCount: number;
  correctCount: number;
  /** FSRS concept key;记忆变化捕获 */
  conceptKey: string;
  stabilityBefore: number | null;
  stabilityAfter: number | null;
  nextReviewAt: number | null;
};

export type SessionPhase = "setup" | "active" | "done";

export type SessionState = {
  phase: SessionPhase;
  mode: SessionMode;
  filters: SessionFilters;
  queue: string[]; // scene_ids
  currentIndex: number;
  results: SessionResult[];
  questionCount: number; // target questions per session
  startedAt: number | null;
  endedAt: number | null;

  // Actions
  configure: (mode: SessionMode, filters: SessionFilters, count: number) => void;
  start: (sceneIds: string[]) => void;
  recordAnswer: (result: SessionResult) => void;
  next: () => void;
  finish: () => void;
  reset: () => void;
};

const INITIAL: Omit<
  SessionState,
  "configure" | "start" | "recordAnswer" | "next" | "finish" | "reset"
> = {
  phase: "setup",
  mode: "mixed",
  filters: {},
  queue: [],
  currentIndex: 0,
  results: [],
  questionCount: 10,
  startedAt: null,
  endedAt: null,
};

export const SESSION_PERSIST_KEY = "gto-train-session-v1";

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      ...INITIAL,

      configure: (mode, filters, count) =>
        set({ mode, filters, questionCount: count, phase: "setup" }),

      start: (sceneIds) =>
        set({
          phase: "active",
          queue: sceneIds,
          currentIndex: 0,
          results: [],
          startedAt: Date.now(),
          endedAt: null,
        }),

      recordAnswer: (result) =>
        set((s) => {
          // 幂等:同一 sceneId 已有记录(刷新回填后二次触发)则不重复追加
          if (s.results.some((r) => r.sceneId === result.sceneId)) return s;
          return { results: [...s.results, result] };
        }),

      next: () =>
        set((s) => {
          const nextIdx = s.currentIndex + 1;
          if (nextIdx >= s.queue.length) {
            return { phase: "done", endedAt: Date.now() };
          }
          return { currentIndex: nextIdx };
        }),

      finish: () => set({ phase: "done", endedAt: Date.now() }),

      reset: () => set({ ...INITIAL }),
    }),
    {
      name: SESSION_PERSIST_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        phase: s.phase,
        mode: s.mode,
        filters: s.filters,
        queue: s.queue,
        currentIndex: s.currentIndex,
        results: s.results,
        questionCount: s.questionCount,
        startedAt: s.startedAt,
        endedAt: s.endedAt,
      }),
    },
  ),
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Interleave scenes from different streets for mixed mode */
export function interleaveScenes(
  scenes: { scene_id: string; street: string }[],
): string[] {
  const byStreet = new Map<string, string[]>();
  for (const s of scenes) {
    const list = byStreet.get(s.street) ?? [];
    list.push(s.scene_id);
    byStreet.set(s.street, list);
  }
  // Round-robin across streets
  const result: string[] = [];
  const iters = [...byStreet.values()].map((ids) => ({ ids, i: 0 }));
  let remaining = true;
  while (remaining) {
    remaining = false;
    for (const iter of iters) {
      if (iter.i < iter.ids.length) {
        result.push(iter.ids[iter.i]);
        iter.i += 1;
        remaining = true;
      }
    }
  }
  return result;
}
